import { createContext, useContext } from "react"

/**
 * Auth bridge for the API layer.
 *
 * `useRequest` reads the bearer token from this context instead of calling
 * Clerk's `useAuth()` directly, which would throw on embed pages that render
 * outside `ClerkProvider`. `ClerkAuthBridge` (in AppProviders) overrides the
 * default for authed pages.
 *
 * Lives in its own leaf module — not in AppProviders — so the generated API
 * clients and `useRequest` can read it without importing the provider tree.
 * That keeps the `api-client` build chunk free of any edge back into the entry
 * chunk's heavy provider graph (which would form a circular chunk dependency).
 *
 * Default returns a null token so API hooks work without crashing pre-auth.
 */
export const ApiAuthContext = createContext<{
  getToken: () => Promise<string | null>
  /**
   * Whether the auth provider has SETTLED — not whether anyone is signed in.
   *
   * 🚨 `getToken` RETURNS `null` WHILE CLERK IS STILL LOADING, AND `useRequest`
   * CANNOT TELL THAT APART FROM "this surface is anonymous". It sends the
   * request with no `Authorization` header either way, the backend answers a
   * 4xx, and `skipRetryOn4xx` refuses to retry a 4xx — so one unlucky mount
   * produces a terminal error on a screen whose data was never unavailable.
   * The user clicks Retry, Clerk has settled by then, and it works. That is the
   * whole shape of the intermittent "Could not load the system map".
   *
   * OPTIONAL, and absent means READY. Embeds render with no `ClerkProvider` and
   * have nothing to wait for, so the default context and every test that builds
   * this value by hand keep issuing requests exactly as before. Only a provider
   * that KNOWS it is still resolving says so, by passing `false`.
   *
   * Read it through {@link useAuthReady} rather than off the context, so the
   * "absent means ready" rule lives in one place instead of at each call site.
   */
  isAuthReady?: boolean
}>({
  getToken: async () => null
})

/**
 * Is the auth provider settled enough to issue an authenticated request?
 *
 * A query gated on this shows its LOADING state while Clerk resolves, which is
 * honest — the data is on its way — instead of its ERROR state, which claims
 * the read failed. Gating the query is deliberately narrower than gating the
 * provider's children: `ClerkAuthBridge` wraps the entire authenticated app, so
 * withholding those would blank every screen's first paint to fix a race on
 * one request.
 */
export function useAuthReady(): boolean {
  return useContext(ApiAuthContext).isAuthReady ?? true
}
